import { randomUUID } from "node:crypto";
import { existsSync, readdirSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import type { Browser, BrowserContext, CDPSession, ElementHandle, Page } from "playwright-core";
import type { PreviewElement, PreviewFrame } from "./preview-rpc-schemas.js";

/**
 * Drives the host's Playwright Chromium as a live, interactive remote browser.
 *
 * Rendering uses a CDP screencast (`Page.startScreencast`) — Chromium pushes
 * JPEG frames as the page paints, which the daemon forwards to the client via
 * an `onFrame` callback (the session layer turns those into `preview/frame_push`
 * messages). Client pointer/keyboard/wheel events come back as `input(...)` and
 * are dispatched as real input, so the page is genuinely interactive (links,
 * forms, animations all work). A separate `pick(...)` path resolves a tapped
 * element to a CSS selector for annotation.
 *
 * Everything rides the existing relay WebSocket — no daemon HTTP. Playwright is
 * a host-side capability that may be absent; entry points throw a typed
 * {@link PreviewServiceError} the session layer surfaces to the client.
 */

const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 800;
const SCREENCAST_QUALITY = 60;
/** Auto-close an idle preview page so we never leak browser tabs. */
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const NAV_TIMEOUT_MS = 30_000;

export type PreviewServiceErrorCode =
  | "playwright_unavailable"
  | "browser_launch_failed"
  | "navigation_failed"
  | "session_not_found"
  | "no_element"
  | "preview_failed";

export class PreviewServiceError extends Error {
  constructor(
    public readonly code: PreviewServiceErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "PreviewServiceError";
  }
}

export interface PreviewFramePayload {
  data: string;
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

export type PreviewInputAction = "move" | "down" | "up" | "click" | "wheel" | "key";

export interface PreviewInputEvent {
  action: PreviewInputAction;
  x?: number;
  y?: number;
  dx?: number;
  dy?: number;
  key?: string;
  text?: string;
}

interface PreviewSession {
  context: BrowserContext;
  page: Page;
  cdp: CDPSession;
  onFrame: (frame: PreviewFramePayload) => void;
  width: number;
  height: number;
  idleTimer: ReturnType<typeof setTimeout>;
}

interface ResolvedElementInfo {
  selector: string;
  path: string;
  tag: string;
  text: string;
  html: string;
  rect: { x: number; y: number; width: number; height: number };
}

/**
 * Element-resolver source, kept as a STRING on purpose. tsx/esbuild's
 * `keepNames` transform wraps named TS functions with a `__name(...)` helper
 * that does not exist in the browser, which makes Playwright's function
 * serialization throw `__name is not defined`. A string is never transformed,
 * so it runs verbatim in the page. Returns ResolvedElementInfo | null.
 */
const RESOLVE_ELEMENT_JS = `(function(px, py){
  var el = document.elementFromPoint(px, py);
  if (!el) return null;
  function esc(v){ return (window.CSS && CSS.escape) ? CSS.escape(v) : v.replace(/[^a-zA-Z0-9_-]/g, "\\\\$&"); }
  function sel(node){
    if (node.id && document.querySelectorAll("#"+esc(node.id)).length===1) return "#"+esc(node.id);
    var parts=[]; var cur=node;
    while(cur && cur.nodeType===1 && cur!==document.documentElement){
      var part=cur.tagName.toLowerCase();
      if(cur.id && document.querySelectorAll("#"+esc(cur.id)).length===1){ parts.unshift("#"+esc(cur.id)); break; }
      var classes=Array.prototype.slice.call(cur.classList).filter(function(c){return c.length>0 && !/\\d{4,}/.test(c);}).slice(0,2).map(function(c){return "."+esc(c);}).join("");
      part+=classes;
      var parent=cur.parentElement;
      if(parent){ var sibs=Array.prototype.slice.call(parent.children).filter(function(c){return c.tagName===cur.tagName;}); if(sibs.length>1){ part+=":nth-of-type("+(sibs.indexOf(cur)+1)+")"; } }
      parts.unshift(part); cur=cur.parentElement;
    }
    return parts.join(" > ");
  }
  function fullPath(node){
    var parts=[]; var cur=node;
    while(cur && cur.nodeType===1){
      var part=cur.tagName.toLowerCase();
      if(cur.id){ part+="#"+esc(cur.id); }
      else {
        var cls=Array.prototype.slice.call(cur.classList).filter(function(c){return c.length>0 && !/\\d{4,}/.test(c);}).slice(0,2);
        if(cls.length){ part+="."+cls.map(esc).join("."); }
      }
      var parent=cur.parentElement;
      if(parent){ var sibs=Array.prototype.slice.call(parent.children).filter(function(c){return c.tagName===cur.tagName;}); if(sibs.length>1){ part+=":nth-of-type("+(sibs.indexOf(cur)+1)+")"; } }
      parts.unshift(part); cur=cur.parentElement;
    }
    return parts.join(" > ");
  }
  var r=el.getBoundingClientRect();
  return { selector: sel(el), path: fullPath(el), tag: el.tagName.toLowerCase(), text:(el.textContent||"").trim().slice(0,400), html: el.outerHTML.slice(0,2000), rect:{x:r.x,y:r.y,width:r.width,height:r.height} };
})`;

/**
 * Resolve the ms-playwright browser cache root for this platform, honoring the
 * `PLAYWRIGHT_BROWSERS_PATH` override (the literal "0" means "next to the npm
 * package" — we ignore that and fall through to the per-OS default, since the
 * bundled daemon has no package dir to look beside).
 */
function resolveBrowsersRoot(): string {
  const override = process.env.PLAYWRIGHT_BROWSERS_PATH;
  if (override && override !== "0") {
    return override;
  }
  const home = homedir();
  switch (process.platform) {
    case "darwin":
      return join(home, "Library", "Caches", "ms-playwright");
    case "win32":
      return join(process.env.LOCALAPPDATA ?? join(home, "AppData", "Local"), "ms-playwright");
    default:
      return join(home, ".cache", "ms-playwright");
  }
}

/**
 * Find a Chromium executable the host already has. We deliberately do NOT rely
 * on playwright-core's own browser registry (its version is pinned to the npm
 * package and won't match a browser the user installed via Python Playwright or
 * a different Node version). Instead we scan the ms-playwright cache for any
 * `chromium-<rev>` build and hand the path to `launch({ executablePath })`,
 * which launches any reasonably-recent Chromium regardless of revision.
 * Returns null when nothing usable is found.
 */
/**
 * Resolve the launcher binary inside a macOS Chromium build dir, tolerating both
 * `chrome-mac`/`chrome-mac-arm64` and the `Chromium.app` vs `Google Chrome for
 * Testing.app` bundle names (the executable matches the `.app` name).
 */
function locateMacExecutable(buildDir: string): string | null {
  let macDirs: string[];
  try {
    macDirs = readdirSync(buildDir).filter((n) => n.startsWith("chrome-mac"));
  } catch {
    return null;
  }
  for (const macDir of macDirs) {
    const appsRoot = join(buildDir, macDir);
    let apps: string[];
    try {
      apps = readdirSync(appsRoot).filter((n) => n.endsWith(".app"));
    } catch {
      continue;
    }
    for (const app of apps) {
      const macosDir = join(appsRoot, app, "Contents", "MacOS");
      let bins: string[];
      try {
        bins = readdirSync(macosDir);
      } catch {
        continue;
      }
      // The launcher binary is the .app name without its extension; fall back to
      // the first entry if that exact name isn't present.
      const preferred = app.slice(0, -4);
      const bin = bins.includes(preferred) ? preferred : bins[0];
      if (bin) {
        return join(macosDir, bin);
      }
    }
  }
  return null;
}

function locateChromiumExecutable(): string | null {
  const explicit = process.env.PLAYWRIGHT_CHROMIUM_EXECUTABLE_PATH ?? process.env.CHROME_PATH;
  if (explicit && existsSync(explicit)) {
    return explicit;
  }

  const root = resolveBrowsersRoot();
  let entries: string[] = [];
  try {
    entries = readdirSync(root);
  } catch {
    entries = [];
  }
  // Prefer the full `chromium-<rev>` build over `chromium_headless_shell-<rev>`,
  // and the highest revision within each kind.
  const builds = entries
    .filter((n) => n.startsWith("chromium-") || n.startsWith("chromium_headless_shell-"))
    .map((name) => ({
      name,
      headless: name.includes("headless_shell"),
      rev: Number(name.match(/(\d+)$/)?.[1] ?? 0),
    }))
    .sort((a, b) => (a.headless !== b.headless ? (a.headless ? 1 : -1) : b.rev - a.rev));

  // macOS layout is variable (`chrome-mac` vs `chrome-mac-arm64`, `Chromium.app`
  // vs `Google Chrome for Testing.app`), so resolve it by walking the bundle
  // rather than guessing a fixed path. Linux/Windows paths are stable.
  const subpaths = [
    join("chrome-linux64", "chrome"),
    join("chrome-linux", "chrome"),
    join("chrome-win", "chrome.exe"),
    join("chrome-linux64", "headless_shell"),
    join("chrome-linux", "headless_shell"),
  ];
  for (const build of builds) {
    const buildDir = join(root, build.name);
    if (process.platform === "darwin") {
      const macExe = locateMacExecutable(buildDir);
      if (macExe) {
        return macExe;
      }
      continue;
    }
    for (const sub of subpaths) {
      const candidate = join(buildDir, sub);
      if (existsSync(candidate)) {
        return candidate;
      }
    }
  }

  // System Chrome/Chromium as a last resort.
  const systemCandidates =
    process.platform === "darwin"
      ? [
          "/Applications/Google Chrome.app/Contents/MacOS/Google Chrome",
          "/Applications/Chromium.app/Contents/MacOS/Chromium",
        ]
      : process.platform === "win32"
        ? [
            join(
              process.env.PROGRAMFILES ?? "C:\\Program Files",
              "Google",
              "Chrome",
              "Application",
              "chrome.exe",
            ),
          ]
        : ["/usr/bin/google-chrome", "/usr/bin/chromium", "/usr/bin/chromium-browser"];
  for (const candidate of systemCandidates) {
    if (existsSync(candidate)) {
      return candidate;
    }
  }
  return null;
}

export class PreviewService {
  private browser: Browser | null = null;
  private launching: Promise<Browser> | null = null;
  private readonly sessions = new Map<string, PreviewSession>();

  private async getBrowser(): Promise<Browser> {
    if (this.browser?.isConnected()) {
      return this.browser;
    }
    if (this.launching) {
      return this.launching;
    }
    this.launching = (async () => {
      let chromium: typeof import("playwright-core").chromium;
      try {
        ({ chromium } = await import("playwright-core"));
      } catch (error) {
        // The Node driver itself is missing — this should not happen now that
        // playwright-core ships as an installer dependency, but surface the real
        // error instead of a misleading "not installed" message.
        throw new PreviewServiceError(
          "playwright_unavailable",
          `Could not load the Playwright driver on this host: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      }
      // Reuse a Chromium the host already has (e.g. from `playwright install
      // chromium`). playwright-core does not download browsers, so without this
      // launch fails with an unhelpful "executable doesn't exist" error.
      const executablePath = locateChromiumExecutable();
      try {
        const browser = await chromium.launch(
          executablePath ? { headless: true, executablePath } : { headless: true },
        );
        this.browser = browser;
        browser.on("disconnected", () => {
          if (this.browser === browser) {
            this.browser = null;
          }
        });
        return browser;
      } catch (error) {
        const detail = error instanceof Error ? error.message : "Failed to launch Chromium.";
        throw new PreviewServiceError(
          "browser_launch_failed",
          executablePath
            ? `Failed to launch Chromium at ${executablePath}: ${detail}`
            : `No Chromium browser found on this host. Install one with \`npx playwright install chromium\` (or set CHROME_PATH to a Chrome/Chromium binary). Underlying error: ${detail}`,
        );
      } finally {
        this.launching = null;
      }
    })();
    return this.launching;
  }

  private getSession(sessionId: string): PreviewSession {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new PreviewServiceError("session_not_found", "Preview session not found or expired.");
    }
    this.touch(sessionId, session);
    return session;
  }

  private touch(sessionId: string, session: PreviewSession): void {
    clearTimeout(session.idleTimer);
    session.idleTimer = setTimeout(() => {
      void this.close(sessionId);
    }, IDLE_TIMEOUT_MS);
  }

  private async startScreencast(session: PreviewSession): Promise<void> {
    // everyNthFrame: 2 halves the frame volume so control RPCs (pick/input)
    // aren't starved on the relay channel, while still feeling live.
    await session.cdp.send("Page.startScreencast", {
      format: "jpeg",
      quality: SCREENCAST_QUALITY,
      maxWidth: session.width,
      maxHeight: session.height,
      everyNthFrame: 2,
    });
  }

  private async captureScreenshotFrame(page: Page): Promise<PreviewFrame> {
    const buffer = await page.screenshot({ type: "jpeg", quality: SCREENCAST_QUALITY });
    const viewport = page.viewportSize() ?? { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    return {
      image: buffer.toString("base64"),
      width: viewport.width,
      height: viewport.height,
      scrollX: 0,
      scrollY: 0,
      pageHeight: viewport.height,
      title: (await page.title()) || null,
      url: page.url(),
    };
  }

  async open(params: {
    url: string;
    width?: number;
    height?: number;
    onFrame: (frame: PreviewFramePayload) => void;
  }): Promise<{ sessionId: string; frame: PreviewFrame }> {
    const browser = await this.getBrowser();
    const width = Math.round(params.width ?? DEFAULT_WIDTH);
    const height = Math.round(params.height ?? DEFAULT_HEIGHT);
    const context = await browser.newContext({
      viewport: { width, height },
      deviceScaleFactor: 1,
      ignoreHTTPSErrors: true,
    });
    const page = await context.newPage();
    try {
      // waitUntil "commit" resolves as soon as the navigation is committed
      // (first response received) instead of blocking on full DOM parse — a
      // live preview shouldn't hang on slow pages; the screencast streams the
      // page in as it paints. Only a genuinely unreachable host times out.
      await page.goto(params.url, { waitUntil: "commit", timeout: NAV_TIMEOUT_MS });
    } catch (error) {
      await context.close().catch(() => {});
      throw new PreviewServiceError(
        "navigation_failed",
        `Could not load ${params.url}: ${
          error instanceof Error ? error.message : "navigation failed"
        }`,
      );
    }
    const previewSessionId = randomUUID();
    const cdp = await context.newCDPSession(page);
    const session: PreviewSession = {
      context,
      page,
      cdp,
      onFrame: params.onFrame,
      width,
      height,
      idleTimer: setTimeout(() => {
        void this.close(previewSessionId);
      }, IDLE_TIMEOUT_MS),
    };
    this.sessions.set(previewSessionId, session);
    // Frame handler must never throw — an uncaught error here would crash the
    // whole daemon process (Playwright dispatches CDP events synchronously).
    cdp.on(
      "Page.screencastFrame",
      (frame: {
        data: string;
        sessionId: number;
        metadata?: { scrollOffsetX?: number; scrollOffsetY?: number };
      }) => {
        try {
          const current = this.sessions.get(previewSessionId);
          if (current) {
            current.onFrame({
              data: frame.data,
              width: current.width,
              height: current.height,
              scrollX: frame.metadata?.scrollOffsetX ?? 0,
              scrollY: frame.metadata?.scrollOffsetY ?? 0,
            });
          }
        } catch {
          // Swallow — a single bad frame must not take down the daemon.
        }
        cdp.send("Page.screencastFrameAck", { sessionId: frame.sessionId }).catch(() => {});
      },
    );
    await this.startScreencast(session);
    const frame = await this.captureScreenshotFrame(page);
    return { sessionId: previewSessionId, frame };
  }

  async frame(sessionId: string): Promise<PreviewFrame> {
    const { page } = this.getSession(sessionId);
    return this.captureScreenshotFrame(page);
  }

  async scroll(sessionId: string, dx: number, dy: number): Promise<PreviewFrame> {
    const { page } = this.getSession(sessionId);
    await page.mouse.wheel(dx, dy);
    await page.waitForTimeout(60);
    return this.captureScreenshotFrame(page);
  }

  /** Dispatch a real input event (interact mode). */
  async input(sessionId: string, event: PreviewInputEvent): Promise<void> {
    const { page } = this.getSession(sessionId);
    switch (event.action) {
      case "move":
        if (typeof event.x === "number" && typeof event.y === "number") {
          await page.mouse.move(event.x, event.y);
        }
        break;
      case "down":
        if (typeof event.x === "number" && typeof event.y === "number") {
          await page.mouse.move(event.x, event.y);
        }
        await page.mouse.down();
        break;
      case "up":
        await page.mouse.up();
        break;
      case "click":
        if (typeof event.x === "number" && typeof event.y === "number") {
          await page.mouse.click(event.x, event.y);
        }
        break;
      case "wheel":
        await page.mouse.wheel(event.dx ?? 0, event.dy ?? 0);
        break;
      case "key":
        if (event.text) {
          await page.keyboard.type(event.text);
        } else if (event.key) {
          await page.keyboard.press(event.key);
        }
        break;
    }
  }

  /** Resize the rendered viewport (devtools-style) and restart the screencast. */
  async resize(sessionId: string, width: number, height: number): Promise<void> {
    const session = this.getSession(sessionId);
    const w = Math.max(200, Math.round(width));
    const h = Math.max(200, Math.round(height));
    await session.cdp.send("Page.stopScreencast").catch(() => {});
    await session.page.setViewportSize({ width: w, height: h });
    session.width = w;
    session.height = h;
    await this.startScreencast(session);
  }

  async pick(sessionId: string, x: number, y: number): Promise<PreviewElement> {
    const session = this.getSession(sessionId);
    const { page } = session;
    // Pause the frame stream so the (larger) pick response isn't starved by
    // a flood of screencast frames competing for the relay channel.
    await session.cdp.send("Page.stopScreencast").catch(() => {});
    try {
      return await this.resolvePick(page, x, y);
    } finally {
      await this.startScreencast(session).catch(() => {});
    }
  }

  private async resolvePick(page: Page, x: number, y: number): Promise<PreviewElement> {
    // All page evaluations use plain strings (never serialized functions) to
    // dodge tsx/esbuild's `__name` wrapper — see RESOLVE_ELEMENT_JS.
    const info = (await page.evaluate(
      `${RESOLVE_ELEMENT_JS}(${x}, ${y})`,
    )) as ResolvedElementInfo | null;
    if (!info) {
      throw new PreviewServiceError("no_element", "No element found at that position.");
    }
    // Outline the element, screenshot it, then restore — all via string evals.
    await page
      .evaluate(
        `(function(){var e=document.elementFromPoint(${x}, ${y}); if(e){ e.setAttribute("data-ap-prev-outline", e.style.outline||""); e.style.outline="3px solid #ff3b30"; e.style.outlineOffset="2px"; }})()`,
      )
      .catch(() => {});
    let image: string;
    const handle = await page.evaluateHandle(`document.elementFromPoint(${x}, ${y})`);
    const element = handle.asElement() as ElementHandle<Element> | null;
    try {
      if (element) {
        const buffer = await element.screenshot({ type: "png", timeout: 5000 });
        image = buffer.toString("base64");
      } else {
        const buffer = await page.screenshot({ type: "png" });
        image = buffer.toString("base64");
      }
    } catch {
      const buffer = await page.screenshot({ type: "png" });
      image = buffer.toString("base64");
    } finally {
      await handle.dispose();
    }
    await page
      .evaluate(
        `(function(){var e=document.elementFromPoint(${x}, ${y}); if(e){ e.style.outline=e.getAttribute("data-ap-prev-outline")||""; e.removeAttribute("data-ap-prev-outline"); }})()`,
      )
      .catch(() => {});
    return { ...info, image };
  }

  async close(sessionId: string): Promise<void> {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return;
    }
    this.sessions.delete(sessionId);
    clearTimeout(session.idleTimer);
    await session.cdp.send("Page.stopScreencast").catch(() => {});
    await session.context.close().catch(() => {});
    // When nothing is being previewed, shut the Chromium process down entirely
    // so an idle annotator holds no RAM. The next open() cold-starts the browser
    // (~0.5s) — a fair trade for not leaking ~100MB while unused. The idle timer
    // also routes here, so an abandoned session reclaims the process too.
    if (this.sessions.size === 0 && this.browser) {
      const browser = this.browser;
      this.browser = null;
      await browser.close().catch(() => {});
    }
  }

  async dispose(): Promise<void> {
    for (const [sessionId] of this.sessions) {
      await this.close(sessionId);
    }
    if (this.browser) {
      await this.browser.close().catch(() => {});
      this.browser = null;
    }
  }
}
